import type { CountdownApiClient } from "@countdown/api-client";
import {
  type ReactElement,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Link, useLocation, useParams } from "react-router";

import { PageHeading } from "@/components/PageHeading";
import { useSaveFirst } from "@/components/SaveBar";
import { SaveForm } from "@/components/SaveForm";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type ActionOutcome, LOAD_FAILED, signInUrl, useResource } from "@/lib/api";
import {
  NUMBER_FORMAT,
  RETENTION_CATEGORY_LABELS,
  formatDateTime,
} from "@/lib/labels";
import {
  type CellPolicy,
  type DomainAddRequest,
  PLATFORM_SIGN_IN_PATH,
  type PlatformCellDetail,
  type PlatformOutcome,
  type PlatformStepOutcome,
  type ProvisioningReport,
  type SuppressionApplication,
  type Verification,
  addDomain,
  applySuppressions,
  deleteCell,
  exportSuppressions,
  isMigratable,
  lines,
  loadCell,
  migrateCell,
  seedCell,
  setCellPolicy,
  setIdentityRealm,
  syncIdentityRealm,
  verifyCell,
} from "@/lib/platform";
import {
  CELL_LOCATION_LABELS,
  DOMAIN_PURPOSE_LABELS,
  OPERATING_MODEL_LABELS,
  POLICY_SWITCH_LABELS,
  PROVISIONING_STATE_LABELS,
  type PolicySwitch,
  STEP_LABELS,
  STEP_STATUS_LABELS,
  SUPPRESSION_REASON_LABELS,
  TENANT_STATUS_LABELS,
} from "@/platform/labels";
import {
  INPUT,
  Notice,
  ReportTable,
  SECTION_HEADING,
  options,
  revisionText,
} from "@/platform/shared";

type Section =
  | "domains"
  | "realm"
  | "migration"
  | "verify"
  | "export"
  | "apply"
  | "seed"
  | "delete";

/** `ReasonRequest`: 3 to 500 characters once trimmed. Checked here so a 422 never shows. */
const REASON_MIN = 3;
const REASON_MAX = 500;

/** An applied or dry-run list, with what was sent: "Übernehmen" repeats exactly that. */
interface Application {
  readonly content: string;
  readonly reason: string;
  readonly result: SuppressionApplication;
}

function field(form: HTMLFormElement | FormData, name: string): string {
  const value = (form instanceof FormData ? form : new FormData(form)).get(name);
  return typeof value === "string" ? value.trim() : "";
}

const POLICY_SWITCHES = Object.entries(POLICY_SWITCH_LABELS) as readonly (readonly [
  PolicySwitch,
  string,
])[];

/** Nothing chosen yet: every key at the platform's behaviour (`policy = {}`). */
export function isUnsetPolicy(policy: CellPolicy): boolean {
  return (
    POLICY_SWITCHES.every(([key]) => policy[key]) &&
    policy.sender_addresses.length === 0 &&
    policy.login_issuers.length === 0 &&
    policy.mail_footer.length === 0 &&
    Object.keys(policy.retention_days).length === 0
  );
}

function stepNotice(outcome: PlatformStepOutcome, done: string): string {
  return (
    outcome.manual_instruction ?? (outcome.completed ? done : "Nicht erledigt.")
  );
}

function CountTable({
  caption,
  rows,
}: {
  readonly caption: string;
  readonly rows: readonly (readonly [string, number])[];
}): ReactElement {
  return (
    <Table className="mt-3">
      <TableCaption className="sr-only">{caption}</TableCaption>
      <TableBody>
        {rows.map(([label, count]) => (
          <TableRow key={label}>
            <TableCell>{label}</TableCell>
            <TableCell className="text-right tabular-nums">
              {NUMBER_FORMAT.format(count)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function VerificationResult({
  report,
}: {
  readonly report: Verification;
}): ReactElement {
  return (
    <div className="mt-4 grid gap-6 md:grid-cols-2">
      <Table className="md:col-span-2">
        <TableCaption className="sr-only">Prüfungen</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Prüfung</TableHead>
            <TableHead>Ergebnis</TableHead>
            <TableHead>Befund</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {report.checks.map((check) => (
            <TableRow key={check.name}>
              <TableCell>{check.name}</TableCell>
              <TableCell>
                {check.passed ? "Bestanden" : "Nicht bestanden"}
              </TableCell>
              <TableCell className="whitespace-pre-wrap">
                {check.detail}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <CountTable
        caption="Zeilen je Tabelle"
        rows={report.counts.map(
          ({ name, count }): readonly [string, number] => [name, count],
        )}
      />
      <CountTable
        caption="Sperrliste"
        rows={[
          ...report.suppressions.map(
            ({ name, count }): readonly [string, number] => [
              SUPPRESSION_REASON_LABELS[name] ?? name,
              count,
            ],
          ),
          ["Gesperrte Konten", report.blocked_accounts],
          ["Unzustellbare Konten", report.bounced_accounts],
        ]}
      />
    </div>
  );
}

function ApplicationResult({
  result,
}: {
  readonly result: SuppressionApplication;
}): ReactElement {
  return (
    <>
      <CountTable
        caption={result.applied ? "Übernommen" : "Probelauf"}
        rows={[
          ["Einträge", result.entries],
          ["Neu eingetragen", result.written],
          ["Schon vorhanden", result.already_present],
          ["Konten gesperrt", result.accounts_blocked],
          ["Konten unzustellbar", result.accounts_bounced],
          ["Abmeldungen", result.unsubscribes],
          ["Wieder gesperrt", result.reblocked.length],
        ]}
      />
      {result.reblocked.length > 0 ? (
        <ul
          className="mt-2 break-all font-mono text-xs"
          aria-label="Wieder gesperrt"
        >
          {result.reblocked.map((hash) => (
            <li key={hash}>{hash}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * One cell: its ledger, and every operation the host CLI offers for a registered cell.
 *
 * Which sections show follows what the API would accept. Verify, the suppression list and
 * the test data reach only an active cell (`find_cell`), the migration only a ready or
 * provisioning one, and the test data only where the session says the seed exists. A button
 * that can only answer 404 is not offered.
 *
 * Deleting, exporting the list and applying it for real want a sign-in younger than ten
 * minutes. The API asks with 401 `reauth`; `@/lib/platform` answers every 401 with the
 * sign-in, which comes back to this page. The dry run has to run again after that, which
 * costs a click and nothing else: it writes nothing.
 */
export function CellPage({
  seedAvailable,
}: {
  readonly seedAvailable: boolean;
}): ReactElement {
  const { slug = "" } = useParams();
  const load = useCallback(
    (api: CountdownApiClient) => loadCell(api, slug),
    [slug],
  );
  const cell = useResource<PlatformCellDetail>(load);
  const { hash } = useLocation();
  const loaded = cell.status === "ready";

  // The backoffice links to `#einstellungen`; the section exists only once the cell loaded,
  // after the browser already looked for the anchor.
  useEffect(() => {
    if (loaded && hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    }
  }, [loaded, hash]);

  const [busy, setBusy] = useState(false);
  const [notices, setNotices] = useState<Partial<Record<Section, string>>>(
    {},
  );
  const [verification, setVerification] = useState<Verification | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [applyReason, setApplyReason] = useState("");
  const [application, setApplication] = useState<Application | null>(null);
  const [confirmSlug, setConfirmSlug] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deletion, setDeletion] = useState<ProvisioningReport | null>(null);
  const saveFirst = useSaveFirst();

  async function run<T>(
    section: Section,
    call: () => Promise<PlatformOutcome<T>>,
    done: (data: T) => string,
  ): Promise<void> {
    setBusy(true);
    setNotices((known) => ({ ...known, [section]: undefined }));
    try {
      const outcome = await call();
      const notice = outcome.ok ? done(outcome.data) : outcome.message;
      setNotices((known) => ({ ...known, [section]: notice }));
    } catch (error: unknown) {
      console.error("[CellPage] call failed", section, error);
      setNotices((known) => ({ ...known, [section]: LOAD_FAILED }));
    } finally {
      setBusy(false);
    }
  }

  function notice(section: Section): ReactElement | null {
    const text = notices[section];
    return text ? <Notice>{text}</Notice> : null;
  }

  const back = (
    <Link className="text-sm underline underline-offset-4" to="/">
      Zellen
    </Link>
  );

  if (cell.status !== "ready") {
    return (
      <>
        {back}
        <div className="mt-4">
          <PageHeading title={slug} />
        </div>
        {cell.status === "loading" ? (
          <p className="text-sm text-muted-foreground">Wird geladen …</p>
        ) : null}
        {cell.status === "not_found" ? (
          <p className="text-sm text-muted-foreground">
            Diese Zelle gibt es nicht.
          </p>
        ) : null}
        {cell.status === "unauthenticated" ? (
          <Button asChild>
            <a href={signInUrl(PLATFORM_SIGN_IN_PATH, window.location)}>
              Anmelden
            </a>
          </Button>
        ) : null}
        {cell.status === "error" || cell.status === "forbidden" ? (
          <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
        ) : null}
      </>
    );
  }

  const detail = cell.data;
  const active = detail.status === "active";
  const retired = detail.provisioning_state === "decommissioned";
  const reasonValid = (value: string): boolean =>
    value.trim().length >= REASON_MIN;
  const deletable = confirmSlug === detail.slug && reasonValid(deleteReason);

  function submitDomain(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    const form = event.currentTarget;
    const hostname = field(form, "hostname");
    // The select offers the label map's keys only.
    const purpose = field(form, "purpose") as DomainAddRequest["purpose"];
    void run(
      "domains",
      () => addDomain(slug, { hostname, purpose }),
      (outcome) => {
        console.log("[CellPage] domain added", slug, hostname);
        form.reset();
        cell.reload();
        return stepNotice(outcome, "Eingetragen.");
      },
    );
  }

  async function savePolicy(form: FormData): Promise<ActionOutcome> {
    const retention: Record<string, number> = {};
    for (const category of Object.keys(detail.defaults.retention_days)) {
      const days = field(form, `retention_${category}`);
      if (days !== "") {
        retention[category] = Number(days);
      }
    }
    const switches = Object.fromEntries(
      POLICY_SWITCHES.map(([key]) => [key, form.get(key) === "on"]),
    ) as Record<PolicySwitch, boolean>;
    const outcome = await setCellPolicy(slug, {
      ...switches,
      sender_addresses: lines(field(form, "sender_addresses")),
      login_issuers: lines(field(form, "login_issuers")),
      mail_footer: lines(field(form, "mail_footer")),
      retention_days: retention,
      reason: field(form, "reason"),
    });
    return outcome.ok ? { ok: true } : outcome;
  }

  function submitExport(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    const form = event.currentTarget;
    const reason = field(form, "reason");
    void run(
      "export",
      () => exportSuppressions(slug, reason),
      (name) => {
        form.reset();
        return `Heruntergeladen: ${name}`;
      },
    );
  }

  function apply(
    content: () => Promise<string>,
    reason: string,
    confirm: boolean,
  ): void {
    void run(
      "apply",
      async () => {
        const text = await content();
        const outcome = await applySuppressions(slug, {
          content: text,
          reason,
          confirm,
        });
        if (outcome.ok) {
          setApplication({ content: text, reason, result: outcome.data });
        }
        return outcome;
      },
      (result) => {
        console.log("[CellPage] suppression list", slug, result.applied);
        return result.applied ? "Übernommen." : "Probelauf, nichts geschrieben.";
      },
    );
  }

  function submitDryRun(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (file !== null) {
      apply(() => file.text(), applyReason.trim(), false);
    }
  }

  function submitDelete(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!deletable) {
      return;
    }
    void run(
      "delete",
      () => deleteCell(slug, confirmSlug, deleteReason.trim()),
      (report) => {
        console.log("[CellPage] cell deleted", slug);
        setDeletion(report);
        setConfirmSlug("");
        setDeleteReason("");
        setVerification(null);
        setApplication(null);
        cell.reload();
        return "Gelöscht.";
      },
    );
  }

  return (
    <>
      {back}
      <div className="mt-4">
        <PageHeading title={detail.name} />
      </div>

      <dl className="grid max-w-2xl grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Kurzname</dt>
        <dd>{detail.slug}</dd>
        <dt className="text-muted-foreground">Status</dt>
        <dd>{TENANT_STATUS_LABELS[detail.status]}</dd>
        <dt className="text-muted-foreground">Einrichtung</dt>
        <dd>{PROVISIONING_STATE_LABELS[detail.provisioning_state]}</dd>
        <dt className="text-muted-foreground">Betriebsmodell</dt>
        <dd>{OPERATING_MODEL_LABELS[detail.operating_model]}</dd>
        <dt className="text-muted-foreground">Standort</dt>
        <dd>{CELL_LOCATION_LABELS[detail.location]}</dd>
      </dl>

      <section className="mt-8">
        <h2 className={SECTION_HEADING}>Schritte</h2>
        <Table className="mt-3">
          <TableCaption className="sr-only">Schritte</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Schritt</TableHead>
              <TableHead>Stand</TableHead>
              <TableHead>Erledigt</TableHead>
              <TableHead>Fehler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.steps.map((step) => (
              <TableRow key={step.name}>
                <TableCell>{STEP_LABELS[step.name] ?? step.name}</TableCell>
                <TableCell>
                  {step.status === null
                    ? "Offen"
                    : STEP_STATUS_LABELS[step.status]}
                </TableCell>
                <TableCell>
                  {step.completed_at === null
                    ? null
                    : formatDateTime(step.completed_at)}
                </TableCell>
                <TableCell className="whitespace-pre-wrap">
                  {step.error}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <section className="mt-8">
        <h2 className={SECTION_HEADING}>Domains</h2>
        <Table className="mt-3">
          <TableCaption className="sr-only">Domains</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Domain</TableHead>
              <TableHead>Zweck</TableHead>
              <TableHead>Primär</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.domains.map((domain) => (
              <TableRow key={domain.hostname}>
                <TableCell>{domain.hostname}</TableCell>
                <TableCell>{DOMAIN_PURPOSE_LABELS[domain.purpose]}</TableCell>
                <TableCell>{domain.is_primary ? "Ja" : "Nein"}</TableCell>
              </TableRow>
            ))}
            {detail.domains.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground">
                  Keine Domains.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {retired ? null : (
          <form
            className="mt-4 grid max-w-2xl gap-4 sm:grid-cols-[1fr_auto_auto] sm:items-end"
            onSubmit={submitDomain}
          >
            <div>
              <label className="block text-sm font-medium" htmlFor="domain-host">
                Domain
              </label>
              <input
                className={INPUT}
                id="domain-host"
                name="hostname"
                required
                maxLength={253}
                autoComplete="off"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium"
                htmlFor="domain-zweck"
              >
                Zweck
              </label>
              <select
                className={INPUT}
                id="domain-zweck"
                name="purpose"
                defaultValue="participant"
              >
                {options(DOMAIN_PURPOSE_LABELS)}
              </select>
            </div>
            <Button type="submit" disabled={busy}>
              Eintragen
            </Button>
          </form>
        )}
        {notice("domains")}
      </section>

      {retired ? null : (
        <section className="mt-8">
          <h2 className={SECTION_HEADING}>Identitätsraum</h2>
          {/* Keyed on the stored value: a reload remounts the field with what was saved.
              The page's bar saves it (22.10.2026). */}
          <div className="mt-3">
            <SaveForm
              key={detail.identity_realm ?? ""}
              label="Identitätsraum"
              readOnly={false}
              submit={async (form) => {
                const name = form.get("identity_realm");
                const outcome = await setIdentityRealm(
                  slug,
                  typeof name === "string" ? name.trim() : "",
                );
                return outcome.ok ? { ok: true } : outcome;
              }}
              onSaved={() => {
                console.log("[CellPage] identity realm set", slug);
                cell.reload();
              }}
            >
              <label className="block text-sm font-medium" htmlFor="realm-name">
                Name
              </label>
              <input
                className={INPUT}
                id="realm-name"
                name="identity_realm"
                defaultValue={detail.identity_realm ?? ""}
                maxLength={200}
                autoComplete="off"
              />
            </SaveForm>
          </div>
          <Button
            className="mt-4"
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              // Works on the stored name: a new one in the field is saved first.
              void saveFirst().then((saved) =>
                saved
                  ? run(
                      "realm",
                      () => syncIdentityRealm(slug),
                      (outcome) => stepNotice(outcome, "Abgeglichen."),
                    )
                  : undefined,
              );
            }}
          >
            Abgleichen
          </Button>
          {notice("realm")}
        </section>
      )}

      {retired ? null : (
        <section className="mt-8" id="einstellungen">
          <h2 className={SECTION_HEADING}>Mandanteneinstellungen</h2>
          {isUnsetPolicy(detail.policy) ? (
            <Notice>
              Noch nichts eingestellt, es gilt das Verhalten der Plattform. Einen
              neuen Mandanten hier einrichten, bevor seine Domains live gehen.
            </Notice>
          ) : null}
          {/* Keyed on the stored values: a reload remounts the fields with what was saved.
              The page's bar saves it, like the identity realm. */}
          <div className="mt-3 max-w-2xl">
            <SaveForm
              key={JSON.stringify(detail.policy)}
              label="Mandanteneinstellungen"
              readOnly={false}
              submit={savePolicy}
              onSaved={() => {
                console.log("[CellPage] policy set", slug);
                cell.reload();
              }}
            >
              <div className="grid gap-2">
                {POLICY_SWITCHES.map(([key, label]) => (
                  <div className="flex items-center gap-2" key={key}>
                    <input
                      id={`policy-${key}`}
                      name={key}
                      type="checkbox"
                      defaultChecked={detail.policy[key]}
                    />
                    <label className="text-sm font-medium" htmlFor={`policy-${key}`}>
                      {label}
                    </label>
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium" htmlFor="policy-absender">
                  Erlaubte Absender
                </label>
                <textarea
                  className={INPUT}
                  id="policy-absender"
                  name="sender_addresses"
                  rows={3}
                  defaultValue={detail.policy.sender_addresses.join("\n")}
                  placeholder={
                    detail.defaults.sender_domains.length > 0
                      ? detail.defaults.sender_domains.map((domain) => `@${domain}`).join("\n")
                      : "Jede Domain"
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium" htmlFor="policy-aussteller">
                  Erlaubte Anmeldedienste
                </label>
                <textarea
                  className={INPUT}
                  id="policy-aussteller"
                  name="login_issuers"
                  rows={3}
                  defaultValue={detail.policy.login_issuers.join("\n")}
                  placeholder={
                    detail.defaults.login_issuers.length > 0
                      ? detail.defaults.login_issuers.join("\n")
                      : (detail.defaults.staff_issuer ?? "")
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium" htmlFor="policy-fusszeile">
                  Fußzeile der E-Mails
                </label>
                <textarea
                  className={INPUT}
                  id="policy-fusszeile"
                  name="mail_footer"
                  rows={6}
                  defaultValue={detail.policy.mail_footer.join("\n")}
                  placeholder={"Firma\nAnschrift\nTelefon · E-Mail · Website\nRegistergericht\nGeschäftsführung"}
                />
              </div>
              <fieldset className="grid gap-2 sm:grid-cols-2">
                <legend className="mb-2 text-sm font-medium">Löschfrist in Tagen</legend>
                {Object.entries(detail.defaults.retention_days).map(
                  ([category, days]) => (
                    <div key={category}>
                      <label
                        className="block text-sm"
                        htmlFor={`policy-frist-${category}`}
                      >
                        {RETENTION_CATEGORY_LABELS[category] ?? category}
                      </label>
                      <input
                        className={INPUT}
                        id={`policy-frist-${category}`}
                        name={`retention_${category}`}
                        type="number"
                        min={1}
                        max={36500}
                        step={1}
                        defaultValue={detail.policy.retention_days[category] ?? ""}
                        placeholder={days === null ? "nicht entschieden" : String(days)}
                      />
                    </div>
                  ),
                )}
              </fieldset>
              <div>
                <label className="block text-sm font-medium" htmlFor="policy-grund">
                  Begründung
                </label>
                <input
                  className={INPUT}
                  id="policy-grund"
                  name="reason"
                  required
                  minLength={REASON_MIN}
                  maxLength={REASON_MAX}
                  autoComplete="off"
                />
              </div>
            </SaveForm>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Gilt ab der nächsten Anfrage für neue Anmeldungen. Die Begründung steht
            im Protokoll des Mandanten.
          </p>
        </section>
      )}

      {isMigratable(detail) ? (
        <section className="mt-8">
          <h2 className={SECTION_HEADING}>Migration</h2>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
            <span>
              Revision{" "}
              {revisionText(detail.current_revision, detail.head_revision)}
            </span>
            {detail.current_revision !== detail.head_revision ? (
              <Button
                type="button"
                disabled={busy}
                onClick={() => {
                  void run(
                    "migration",
                    () => migrateCell(slug),
                    (result) => {
                      console.log("[CellPage] migrated", slug, result.revision);
                      cell.reload();
                      return result.revision
                        ? `Migriert auf ${result.revision}.`
                        : "Migriert.";
                    },
                  );
                }}
              >
                Migrieren
              </Button>
            ) : null}
          </div>
          {notice("migration")}
        </section>
      ) : null}

      {active ? (
        <section className="mt-8">
          <h2 className={SECTION_HEADING}>Prüfung</h2>
          <Button
            className="mt-3"
            type="button"
            disabled={busy}
            onClick={() => {
              void run(
                "verify",
                () => verifyCell(slug),
                (report) => {
                  setVerification(report);
                  return report.ok ? "Bestanden." : "Nicht bestanden.";
                },
              );
            }}
          >
            Prüfen
          </Button>
          {notice("verify")}
          {verification !== null ? (
            <VerificationResult report={verification} />
          ) : null}
        </section>
      ) : null}

      {active ? (
        <section className="mt-8">
          <h2 className={SECTION_HEADING}>Sperrliste</h2>

          <form
            className="mt-3 grid max-w-2xl gap-4 sm:grid-cols-[1fr_auto] sm:items-end"
            onSubmit={submitExport}
          >
            <div>
              <label
                className="block text-sm font-medium"
                htmlFor="export-grund"
              >
                Begründung für den Export
              </label>
              <input
                className={INPUT}
                id="export-grund"
                name="reason"
                required
                minLength={REASON_MIN}
                maxLength={REASON_MAX}
                autoComplete="off"
              />
            </div>
            <Button type="submit" disabled={busy}>
              Herunterladen
            </Button>
          </form>
          {notice("export")}

          <form className="mt-6 grid max-w-2xl gap-4" onSubmit={submitDryRun}>
            <div>
              <label className="block text-sm font-medium" htmlFor="apply-datei">
                Datei
              </label>
              <input
                className={INPUT}
                id="apply-datei"
                type="file"
                accept=".ndjson,.jsonl,application/x-ndjson,text/plain"
                required
                // Both inputs lock while a run is out: its answer is kept with what was
                // sent, and "Übernehmen" repeats exactly that.
                disabled={busy}
                onChange={(event) => {
                  setFile(event.currentTarget.files?.[0] ?? null);
                  setApplication(null);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium" htmlFor="apply-grund">
                Begründung für die Übernahme
              </label>
              <input
                className={INPUT}
                id="apply-grund"
                required
                minLength={REASON_MIN}
                maxLength={REASON_MAX}
                autoComplete="off"
                disabled={busy}
                value={applyReason}
                onChange={(event) => {
                  setApplyReason(event.currentTarget.value);
                  setApplication(null);
                }}
              />
            </div>
            <div className="flex flex-wrap gap-4">
              <Button
                type="submit"
                variant="outline"
                disabled={busy || file === null || !reasonValid(applyReason)}
              >
                Probelauf
              </Button>
              {application !== null && !application.result.applied ? (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const { content, reason } = application;
                    apply(() => Promise.resolve(content), reason, true);
                  }}
                >
                  Übernehmen
                </Button>
              ) : null}
            </div>
          </form>
          {notice("apply")}
          {application !== null ? (
            <ApplicationResult result={application.result} />
          ) : null}
        </section>
      ) : null}

      {active && seedAvailable ? (
        <section className="mt-8">
          <h2 className={SECTION_HEADING}>Testdaten</h2>
          <form
            className="mt-3 grid max-w-2xl gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              const drawEligible =
                new FormData(event.currentTarget).get("draw_eligible") === "on";
              void run(
                "seed",
                () => seedCell(slug, drawEligible),
                (result) =>
                  `${result.created_anything ? "Angelegt" : "Schon vorhanden"}: ${result.action_slug}${result.action_is_test ? ", Testaktion" : ""}`,
              );
            }}
          >
            <div className="flex items-center gap-2">
              <input id="seed-ziehung" name="draw_eligible" type="checkbox" />
              <label className="text-sm font-medium" htmlFor="seed-ziehung">
                Für die Ziehung zählen
              </label>
            </div>
            <div>
              <Button type="submit" disabled={busy}>
                Testdaten anlegen
              </Button>
            </div>
          </form>
          {notice("seed")}
        </section>
      ) : null}

      <details className="mt-8 max-w-2xl">
        <summary className="cursor-pointer font-semibold text-destructive">
          Löschen
        </summary>
        <form className="mt-4 grid gap-4" onSubmit={submitDelete}>
          <div>
            <label className="block text-sm font-medium" htmlFor="delete-slug">
              Kurzname
            </label>
            <input
              className={INPUT}
              id="delete-slug"
              required
              autoComplete="off"
              value={confirmSlug}
              onChange={(event) => {
                setConfirmSlug(event.currentTarget.value);
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="delete-grund">
              Begründung für das Löschen
            </label>
            <input
              className={INPUT}
              id="delete-grund"
              required
              minLength={REASON_MIN}
              maxLength={REASON_MAX}
              autoComplete="off"
              value={deleteReason}
              onChange={(event) => {
                setDeleteReason(event.currentTarget.value);
              }}
            />
          </div>
          <div>
            <Button
              type="submit"
              variant="destructive"
              disabled={busy || !deletable}
            >
              Endgültig löschen
            </Button>
          </div>
        </form>
        {notice("delete")}
        {deletion !== null ? <ReportTable report={deletion} /> : null}
      </details>
    </>
  );
}
